import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiPlus, FiMapPin, FiTrash2, FiCheck } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import API from '../api/axios';
import LocationPicker from '../components/LocationPicker';
import { formatPrice, STORE_LOCATION } from '../utils/constants';
import { getDistanceKm } from '../utils/location';
import './Checkout.css';

const COLOR_LABEL = { bw: 'Black & White', colour: 'Colour' };
const ORIENTATION_LABEL = { portrait: 'Portrait', landscape: 'Landscape' };

const getFileIcon = (name = '') => {
  const ext = name.split('.').pop().toLowerCase();
  const map = { pdf: '📄', jpg: '🖼️', jpeg: '🖼️', png: '🖼️', webp: '🖼️', doc: '📝', docx: '📝', xls: '📊', xlsx: '📊', ppt: '📽️', pptx: '📽️' };
  return map[ext] || '📁';
};

const isImageExt = (name = '') => ['jpg', 'jpeg', 'png', 'webp'].includes(name.split('.').pop().toLowerCase());

const formatPageRange = (pages) => {
  if (!pages || !pages.length) return '';
  const sorted = [...pages].sort((a, b) => a - b);
  const ranges = [];
  let start = sorted[0], end = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === end + 1) end = sorted[i];
    else {
      ranges.push(start === end ? `${start}` : `${start}-${end}`);
      start = end = sorted[i];
    }
  }
  ranges.push(start === end ? `${start}` : `${start}-${end}`);
  return ranges.join(', ');
};

const STORE_HOURS = { open: 9, close: 21, label: '9:00 AM – 9:00 PM' };
const getStoreStatus = () => {
  const now = new Date();
  const utcMs = now.getTime() + (now.getTimezoneOffset() * 60000);
  const istDate = new Date(utcMs + (5.5 * 3600000));
  const hour = istDate.getHours();
  const isOpen = hour >= STORE_HOURS.open && hour < STORE_HOURS.close;
  const isNight = hour >= STORE_HOURS.close;
  return {
    isOpen,
    isNight,
    deliveryTitle: isOpen ? 'Delivery in 30 minutes' : (isNight ? 'Store Closed · Next Day Delivery' : 'Store Closed · Morning Delivery'),
    deliverySub: isOpen ? 'Fast local delivery' : (isNight ? 'Will be delivered tomorrow morning by 10:00 AM' : 'Will be delivered today morning by 10:00 AM'),
    storeHoursLabel: STORE_HOURS.label,
  };
};

const Checkout = () => {
  const navigate = useNavigate();
  const storeStatus = getStoreStatus();
  const { user, cart, addToCart, removeFromCart, clearCart, updateUser, cartTotal, cartCount } = useAuth();
  const [recentlyRemoved, setRecentlyRemoved] = useState(null);
  const [cancellationSeconds, setCancellationSeconds] = useState(null);
  const undoTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    };
  }, []);

  // Fetch cancellation policy from admin settings
  useEffect(() => {
    const fetchCancellationPolicy = async () => {
      try {
        const { data } = await API.get('/print-rates');
        if (data.success && data.rates) {
          const rate = data.rates.find(r => r.key === 'order_cancellation_seconds');
          if (rate) setCancellationSeconds(Number(rate.value));
        }
      } catch (err) {
        console.error('Failed to fetch cancellation policy:', err);
      }
    };
    fetchCancellationPolicy();
  }, []);

  const handleRemoveItem = (item) => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setRecentlyRemoved(item);
    removeFromCart(item.id, item.type);
    toast.success(`${item.name} removed from cart`);

    undoTimerRef.current = setTimeout(() => {
      setRecentlyRemoved(null);
    }, 5000);
  };

  const handleUndoRemove = () => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    if (recentlyRemoved) {
      addToCart({ ...recentlyRemoved, quantity: recentlyRemoved.quantity });
      toast.success(`${recentlyRemoved.name} restored to cart!`);
      setRecentlyRemoved(null);
    }
  };

  const handleDismissRemove = () => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setRecentlyRemoved(null);
  };
  const [placing, setPlacing] = useState(false);
  const [showLocation, setShowLocation] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [deliveryRates, setDeliveryRates] = useState({ baseFee: 20, threshold: 99, perKm: 0 });

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const { data } = await API.get('/print-rates');
        if (data.success && data.rates) {
          const find = (key, fallback) => data.rates.find(r => r.key === key)?.value ?? fallback;
          setDeliveryRates({
            baseFee: find('delivery_base_fee', 20),
            threshold: find('delivery_free_threshold', 99),
            perKm: find('delivery_charge_per_km', 0)
          });
        }
      } catch (err) {
        console.warn('Failed to fetch print rates in checkout:', err);
      }
    };
    fetchRates();
  }, []);

  const distanceKm = user?.address?.lat ? getDistanceKm(user.address.lat, user.address.lng, STORE_LOCATION.lat, STORE_LOCATION.lng) : 0;
  const deliveryFee = cartTotal >= deliveryRates.threshold ? 0 : Math.round(deliveryRates.baseFee + distanceKm * deliveryRates.perKm);
  const grandTotal = cartTotal + deliveryFee;

  const hasPrintItems = cart.some(i => i.type === 'print');
  const hasAddress = !!user?.address?.lat && !!user?.address?.line1;
  const savedAddresses = user?.savedAddresses || [];

  const getDistanceMetres = (lat, lng) => {
    const km = getDistanceKm(lat, lng, STORE_LOCATION.lat, STORE_LOCATION.lng);
    return Math.round(km * 1000);
  };

  const handleSelectSavedAddress = async (addr) => {
    try {
      const { data } = await API.put(`/users/address/select/${addr._id}`);
      updateUser({ address: data.address });
      toast.success('Delivery address updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to select address');
    }
  };

  const handleDeleteSavedAddress = async (addrId, e) => {
    e.stopPropagation();
    try {
      const { data } = await API.delete(`/users/address/${addrId}`);
      updateUser({ savedAddresses: data.savedAddresses });
      toast.success('Address removed');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    }
  };

  useEffect(() => {
    API.get('/products?limit=10')
      .then(({ data }) => setSuggestions(data.products || []))
      .catch(() => {});
  }, []);

  const getRazorpay = () => {
    if (!window.Razorpay) {
      toast.error('Razorpay SDK failed to load. Refresh and try again.');
      return null;
    }
    return window.Razorpay;
  };

  const executeOrderPlacement = async (currentBalance) => {
    try {
      const isObjectId = (id) => /^[0-9a-fA-F]{24}$/.test(id);

      const items = cart.map(item => {
        const base = {
          type: item.type,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          options: item.printConfig || item.options || {},
          customNote: item.customNote || '',
          fileUrl: item.fileUrl || '',
        };
        if (item.type === 'mart') {
          if (isObjectId(item.id)) base.productId = item.id;
        } else if (item.driveFileId) {
          base.driveFileId = item.driveFileId;
        } else {
          if (isObjectId(item.id)) base.productId = item.id;
        }
        return base;
      });

      const { data } = await API.post('/orders', { items });
      toast.success(`Order placed successfully! 🎉 ${data.order.orderId}`);
      const newBalance = currentBalance - grandTotal;
      updateUser({ walletBalance: newBalance, numOrders: (user?.numOrders || 0) + 1 });
      clearCart();
      navigate('/orders');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to place order');
    }
  };

  const handleDirectPayment = async () => {
    const gap = grandTotal - (user?.walletBalance || 0);
    const RazorpayCheckout = getRazorpay();
    if (!RazorpayCheckout) return;

    setPlacing(true);
    try {
      const { data } = await API.post('/create-order', {
        amount: Math.round(gap * 100),
        currency: 'INR',
        receipt: `order_direct_${Date.now()}`,
      });

      const razorpayKeyId = data.key_id;
      if (!razorpayKeyId) {
        toast.error('Razorpay key not configured. Contact support.');
        setPlacing(false);
        return;
      }

      const options = {
        key: razorpayKeyId,
        amount: data.amount,
        currency: data.currency,
        name: 'Shubink',
        description: `Direct Order Payment Gap ₹${gap}`,
        order_id: data.order_id,
        prefill: {
          name: user?.name || '',
          contact: user?.phone || '',
        },
        theme: {
          color: '#1f2937',
        },
        handler: async function (response) {
          try {
            const verifyRes = await API.post('/verify-payment', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            updateUser({ walletBalance: verifyRes.data.balance });
            toast.success('Payment verified successfully!');
            await executeOrderPlacement(verifyRes.data.balance);
          } catch (verifyError) {
            toast.error(verifyError.response?.data?.error || 'Payment verification failed');
          } finally {
            setPlacing(false);
          }
        },
        modal: {
          ondismiss: function () {
            setPlacing(false);
            toast('Payment cancelled');
          },
        },
      };

      const rzp = new RazorpayCheckout(options);
      rzp.on('payment.failed', function (response) {
        setPlacing(false);
        const reason = response?.error?.description || 'Payment failed';
        toast.error(reason);
      });
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to initiate payment');
      setPlacing(false);
    }
  };

  const placeOrder = async () => {
    if (!hasAddress) {
      setShowLocation(true);
      return;
    }
    if ((user?.walletBalance || 0) < grandTotal) {
      await handleDirectPayment();
      return;
    }

    setPlacing(true);
    await executeOrderPlacement(user?.walletBalance || 0);
    setPlacing(false);
  };

  const handleEditItem = (item) => {
    if (item.type === 'print' && item.driveFileId) {
      navigate('/print-store', {
        state: {
          editFile: {
            id: item.id,
            name: item.name,
            viewLink: item.fileUrl,
            thumbnailLink: item.image,
            pageCount: item.pageCount,
            mimeType: item.mimeType,
            config: item.printConfig
          },
          editCartItem: item
        }
      });
    } else if (item.id && String(item.id).startsWith('passport-')) {
      removeFromCart(item.id, item.type);
      navigate('/passport-photos');
    } else if (item.type === 'mart') {
      navigate(`/product/${item.id}`);
    } else {
      navigate(-1);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="page chk-page">
        <div className="chk-header">
          <button className="chk-back" onClick={() => navigate(-1)}><FiArrowLeft size={20} /></button>
          <span className="chk-header-title">Checkout</span>
        </div>
        <div className="chk-empty">
          <div className="chk-empty-icon">🛒</div>
          <p>Your cart is empty</p>
          <button className="btn btn-primary" onClick={() => navigate('/')}>Shop Now</button>
        </div>
      </div>
    );
  }

  const totalPages = cart.filter(i => i.type === 'print').reduce((s, i) => {
    const isDouble = i.printConfig?.sides === 'both';
    const sheets = isDouble ? Math.ceil((i.printConfig?.pages || 1) / 2) : (i.printConfig?.pages || 1);
    return s + sheets * (i.printConfig?.copies || 1);
  }, 0);
  const itemCount = cartCount;

  return (
    <div className="chk-page">
      {/* Header */}
      <div className="chk-header">
        <button className="chk-back" onClick={() => navigate(-1)}><FiArrowLeft size={20} /></button>
        <span className="chk-header-title">Checkout</span>
        <button className="chk-clear" onClick={() => { clearCart(); navigate(-1); }}>Clear cart</button>
      </div>

      {/* Delivery Card */}
      <div className={`chk-delivery-card ${!storeStatus.isOpen ? 'chk-delivery-closed' : ''}`} style={!storeStatus.isOpen ? { borderColor: '#d97706', background: 'rgba(217, 119, 6, 0.05)' } : {}}>
        <div className="chk-delivery-icon">{storeStatus.isOpen ? '🕐' : '🌙'}</div>
        <div className="chk-delivery-info">
          <span className="chk-delivery-time" style={!storeStatus.isOpen ? { color: '#d97706' } : {}}>{storeStatus.deliveryTitle}</span>
          <span className="chk-delivery-sub">
            {!storeStatus.isOpen
              ? `${storeStatus.deliverySub} (Store Hours: ${storeStatus.storeHoursLabel})`
              : totalPages > 0
                ? `${totalPages} page${totalPages !== 1 ? 's' : ''}`
                : `${itemCount} item${itemCount !== 1 ? 's' : ''}`}
          </span>
        </div>
        <div className="chk-delivery-wavy" />
      </div>

      {hasPrintItems && storeStatus.isOpen && (
        <div className="chk-print-notice">
          Delivery time may have increased due to print items.
        </div>
      )}

      {/* Cart Items */}
      <div className="chk-items">
        {cart.map((item, idx) => {
          const cfg = item.printConfig;
          const specs = cfg
            ? [
                cfg.copies > 1 ? `${cfg.copies} copies` : null,
                `${cfg.pages} page${cfg.pages !== 1 ? 's' : ''}`,
                cfg.pageSelection === 'range' && cfg.pageRange ? (typeof cfg.pageRange === 'string' ? `Pages ${cfg.pageRange}` : `Pages ${cfg.pageRange.start}-${cfg.pageRange.end}`) : null,
                cfg.pageSelection === 'custom' && cfg.customPages ? `Pages ${formatPageRange(cfg.customPages)}` : null,
                COLOR_LABEL[cfg.color] || cfg.color,
                cfg.sides === 'single' ? 'One side' : cfg.sides === 'both' ? 'Both sides' : cfg.sides || null,
                ORIENTATION_LABEL[cfg.orientation] || cfg.orientation,
                cfg.scale ? `Scale: ${cfg.scale}%` : null
              ].filter(Boolean).join(', ')
            : null;

          const hasThumb = item.image && (item.type === 'mart' || isImageExt(item.name || ''));

          return (
            <div key={`${item.type}-${item.id}-${idx}`} className="chk-item">
              {/* Thumbnail */}
              <div className="chk-item-thumb">
                {hasThumb ? (
                  <img
                    src={item.image}
                    alt={item.name}
                    className="chk-thumb-img"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'inline';
                    }}
                  />
                ) : null}
                <span className="chk-thumb-icon" style={{ display: hasThumb ? 'none' : 'inline' }}>
                  {item.type === 'print' ? getFileIcon(item.name) : '📦'}
                </span>
              </div>

              {/* Info */}
              <div className="chk-item-body">
                <div className="chk-item-name">
                  {item.type === 'print'
                    ? `File ${idx + 1} - ${(item.driveFileId || item.id || '').slice(0, 28)}…`
                    : item.name}
                </div>
                {specs && <div className="chk-item-specs">{specs}</div>}
                {item.type === 'mart' && item.quantity > 1 && (
                  <div className="chk-item-specs">Qty: {item.quantity}</div>
                )}
                 <button
                  className="chk-item-remove"
                  onClick={() => handleRemoveItem(item)}
                >
                  Remove
                </button>
              </div>

              {/* Right */}
              <div className="chk-item-right">
                <button className="chk-edit-btn" onClick={() => handleEditItem(item)}>Edit</button>
                <div className="chk-item-price">
                  {item.mrp && item.mrp > item.price && (
                    <span className="chk-price-mrp">₹{item.mrp * item.quantity}</span>
                  )}
                  <span className="chk-price-final">₹{item.price * item.quantity}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add More Items */}
      <div className="chk-add-more">
        <button className="chk-add-more-btn" onClick={() => navigate('/')}>
          <FiPlus size={16} />
          <span>Add More Items</span>
        </button>
      </div>

      {/* You might also like */}
      {suggestions.length > 0 && (
        <div className="chk-suggestions">
          <div className="chk-section-title">You might also like</div>
          <div className="chk-suggest-scroll">
            {suggestions.map(p => (
              <div key={p._id} className="chk-suggest-card" onClick={() => navigate(`/product/${p._id}`)}>
                <div className="chk-suggest-img">
                  {p.image ? (
                    <img
                      src={p.image}
                      alt={p.name}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'inline';
                      }}
                    />
                  ) : null}
                  <span style={{ display: p.image ? 'none' : 'inline' }}>📦</span>
                </div>
                <div className="chk-suggest-name">{p.name}</div>
                <div className="chk-suggest-price">₹{p.price}</div>
                <button
                  className="chk-suggest-add"
                  onClick={e => { e.stopPropagation(); navigate(`/product/${p._id}`); }}
                >
                  <FiPlus size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Delivery Address */}
      <div className="chk-addr-section">
        <div className="chk-addr-header-row">
          <span className="chk-addr-label">📍 Delivery Address <span className="chk-addr-required">*</span></span>
        </div>

        {/* Active address */}
        {hasAddress && (
          <div className="chk-addr-card chk-addr-card--active">
            <div className="chk-addr-card-left">
              <FiCheck size={14} className="chk-addr-card-check" />
              <div>
                <div className="chk-addr-card-line1">{user.address.line1}</div>
                {user.address.line2 && <div className="chk-addr-card-line2">{user.address.line2}</div>}
                {(user.address.receiverName || user.address.alternatePhone) && (
                  <div className="chk-addr-card-receiver" style={{ fontSize: '11px', marginTop: '4px', color: 'var(--text-secondary)' }}>
                    👤 {user.address.receiverName} {user.address.alternatePhone && `· +91 ${user.address.alternatePhone}`}
                  </div>
                )}
              </div>
            </div>
            <div className="chk-addr-card-right">
              <span className="chk-addr-distance">
                {getDistanceMetres(user.address.lat, user.address.lng)}m from store
              </span>
            </div>
          </div>
        )}

        {/* Saved addresses (excluding the currently active one) */}
        {savedAddresses.filter(sa => {
          if (!user?.address?.lat) return true;
          const d = getDistanceKm(sa.lat, sa.lng, user.address.lat, user.address.lng);
          return d > 0.05;
        }).map(addr => (
          <div
            key={addr._id}
            className="chk-addr-card"
            onClick={() => handleSelectSavedAddress(addr)}
          >
            <div className="chk-addr-card-left">
              <FiMapPin size={14} className="chk-addr-card-pin" />
              <div>
                 <div className="chk-addr-card-line1">{addr.line1}</div>
                 {addr.line2 && <div className="chk-addr-card-line2">{addr.line2}</div>}
                 {(addr.receiverName || addr.alternatePhone) && (
                   <div className="chk-addr-card-receiver" style={{ fontSize: '11px', marginTop: '4px', color: 'var(--text-secondary)' }}>
                     👤 {addr.receiverName} {addr.alternatePhone && `· +91 ${addr.alternatePhone}`}
                   </div>
                 )}
              </div>
            </div>
            <div className="chk-addr-card-right">
              <span className="chk-addr-distance">
                {getDistanceMetres(addr.lat, addr.lng)}m
              </span>
              <button
                className="chk-addr-delete-btn"
                onClick={(e) => handleDeleteSavedAddress(addr._id, e)}
              >
                <FiTrash2 size={13} />
              </button>
            </div>
          </div>
        ))}

        {/* Add new address button */}
        <button className="chk-addr-add-btn" onClick={() => setShowLocation(true)}>
          <FiPlus size={16} />
          <span>Add New Address</span>
        </button>
      </div>

      {/* Free Delivery Progress Banner */}
      <div className="chk-free-delivery-banner" style={{
        background: cartTotal >= deliveryRates.threshold ? 'rgba(0, 217, 166, 0.08)' : 'rgba(108, 99, 255, 0.08)',
        border: cartTotal >= deliveryRates.threshold ? '1px dashed #00D9A6' : '1px dashed var(--primary)',
        borderRadius: 'var(--radius-md)',
        padding: '12px 16px',
        marginTop: '24px',
        marginBottom: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        fontSize: '13px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '600' }}>
          {cartTotal >= deliveryRates.threshold ? (
            <span style={{ color: '#00D9A6' }}>
              🎉 Yeah! You got FREE Delivery!
            </span>
          ) : (
            <span style={{ color: 'var(--text-primary)' }}>
              🎉 Add <span style={{ color: 'var(--primary-light)', fontWeight: '800' }}>₹{deliveryRates.threshold - cartTotal}</span> more for <span style={{ color: '#00D9A6', fontWeight: '800' }}>FREE Delivery</span>!
            </span>
          )}
          <span style={{ color: 'var(--text-secondary)' }}>
            {Math.min(100, Math.round((cartTotal / deliveryRates.threshold) * 100))}%
          </span>
        </div>
        <div style={{
          background: 'rgba(255, 255, 255, 0.06)',
          height: '6px',
          borderRadius: '3px',
          overflow: 'hidden'
        }}>
          <div style={{
            background: cartTotal >= deliveryRates.threshold
              ? 'linear-gradient(90deg, #00D9A6 0%, #3bf0c6 100%)'
              : 'linear-gradient(90deg, var(--primary) 0%, var(--primary-light) 100%)',
            width: `${Math.min(100, (cartTotal / deliveryRates.threshold) * 100)}%`,
            height: '100%',
            borderRadius: '3px',
            transition: 'width 0.3s ease'
          }} />
        </div>
      </div>

      {/* Billing Summary */}
      <div className="chk-billing-card" style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-md)', padding: '16px', border: '1px solid var(--border-color)', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: '800', marginBottom: '12px', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px' }}>Billing Summary</h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
          <span style={{ color: 'var(--text-secondary)' }}>Items Subtotal</span>
          <span>₹{cartTotal}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
          <span style={{ color: 'var(--text-secondary)' }}>Delivery Charges</span>
          <span style={{ color: deliveryFee === 0 ? '#00D9A6' : 'var(--text-primary)', fontWeight: deliveryFee === 0 ? '700' : 'normal' }}>
            {deliveryFee === 0 ? 'FREE' : `₹${deliveryFee}`}
          </span>
        </div>
        {distanceKm > 0 && (
          <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginBottom: '8px', textAlign: 'right' }}>
            Distance: {distanceKm.toFixed(1)} km
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '800', fontSize: '15px', borderTop: '1px dashed var(--border-color)', paddingTop: '10px', marginTop: '10px' }}>
          <span>Grand Total</span>
          <span style={{ color: 'var(--accent-color)' }}>₹{grandTotal}</span>
        </div>
      </div>

      {/* Store Credit info */}
      <div className="chk-wallet-row">
        <span>Store Credit Balance</span>
        <span style={{ color: (user?.walletBalance || 0) >= grandTotal ? '#00D9A6' : '#ff4d4d', fontWeight: 700 }}>
          {formatPrice(user?.walletBalance || 0)}
        </span>
      </div>

      {/* Cancellation Policy Notice */}
      {cancellationSeconds && (
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px', textAlign: 'center' }}>
          You can cancel this order within {cancellationSeconds} seconds of placing.
        </div>
      )}

      {/* Bottom CTA */}
      <div className="chk-bottom">
        <div className="chk-bottom-info">
          <span className="chk-bottom-items">{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
          <span className="chk-bottom-total">₹{grandTotal}</span>
        </div>
        <button
          className="chk-bottom-btn"
          onClick={hasAddress ? placeOrder : () => setShowLocation(true)}
          disabled={placing}
        >
          {placing
            ? 'Processing...'
            : !hasAddress
            ? 'Add address to continue →'
            : (user?.walletBalance || 0) >= grandTotal
            ? `Pay ₹${grandTotal} (Store Credit) →`
            : (user?.walletBalance || 0) === 0
            ? `Pay ₹${grandTotal} via Razorpay →`
            : `Pay Remaining ₹${grandTotal - (user?.walletBalance || 0)} via Razorpay →`}
        </button>
      </div>

      {/* Spacer */}
      <div style={{ height: 90 }} />

      {/* Location Picker */}
      {showLocation && (
        <LocationPicker
          onClose={() => {
            setShowLocation(false);
          }}
        />
      )}

      {recentlyRemoved && (
        <div className="cart-undo-toast slide-up" style={{ zIndex: 10000 }}>
          <span className="cart-undo-toast-text">Removed {recentlyRemoved.name}</span>
          <button className="cart-undo-toast-btn" onClick={handleUndoRemove}>Undo</button>
          <button className="cart-undo-toast-close" onClick={handleDismissRemove}>✕</button>
        </div>
      )}
    </div>
  );
};

export default Checkout;
